import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, formatError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import CityWeatherCard from "@/components/CityWeatherCard";
import GauravLeaderboard from "@/components/GauravLeaderboard";
import FeaturedWeekCard from "@/components/FeaturedWeekCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Send, User, MapPin, Phone, Loader2, ImageIcon, Heart, X, Copy, Check, Hand } from "lucide-react";

export default function ContributorPage() {
  const [wall, setWall] = useState([]);
  const [cities, setCities] = useState([]);
  const [activeCity, setActiveCity] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", city: "", text: "" });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");
  const fileRef = useRef();
  const formRef = useRef();
  const [showInvite, setShowInvite] = useState(false);
  const [copied, setCopied] = useState(false);
  const [myRefCode, setMyRefCode] = useState(() => {
    try { return localStorage.getItem("in21_my_ref") || ""; } catch { return ""; }
  });
  const [clapped, setClapped] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("in21_clapped") || "[]")); } catch { return new Set(); }
  });
  const [clapCounts, setClapCounts] = useState({});

  const clap = async (id) => {
    if (clapped.has(id)) return toast.error("आप पहले ही ताली दे चुके हैं");
    // optimistic update
    setClapped((prev) => {
      const next = new Set(prev); next.add(id);
      try { localStorage.setItem("in21_clapped", JSON.stringify([...next])); } catch {}
      return next;
    });
    setClapCounts((c) => ({ ...c, [id]: (c[id] ?? 0) + 1 }));
    try {
      const { data } = await api.post(`/contributor/${id}/clap`);
      setClapCounts((c) => ({ ...c, [id]: data.claps }));
    } catch (e) { toast.error(formatError(e)); }
  };

  // Capture incoming referral code from URL and persist it for the submit.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const r = url.searchParams.get("ref");
      if (r && r.trim()) {
        localStorage.setItem("in21_incoming_ref", r.trim());
      }
    } catch {}
  }, []);

  const load = (city = activeCity) => {
    setLoading(true);
    const params = city ? { city } : {};
    return Promise.all([
      api.get("/contributor/wall", { params }).then((r) => setWall(r.data)),
      api.get("/contributor/cities").then((r) => setCities(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);

  const pickCity = (city) => {
    setActiveCity(city);
    load(city);
  };

  const quickSubmit = (city) => {
    setForm((f) => ({ ...f, city }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    toast.success(`"${city}" के लिए रिपोर्ट फ़ॉर्म तैयार है — अब भरें और भेजें`);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickPhoto = (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("फ़ोटो 6MB से बड़ी है");
    setPhoto(file);
    const r = new FileReader();
    r.onload = () => setPreview(r.result);
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return toast.error("नाम और विवरण भरें");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("text", form.text);
      if (form.phone) fd.append("phone", form.phone);
      if (form.city) fd.append("city", form.city);
      let incoming = "";
      try { incoming = localStorage.getItem("in21_incoming_ref") || ""; } catch {}
      if (incoming) fd.append("referred_by", incoming);
      if (photo) fd.append("file", photo);
      const { data: subRes } = await api.post("/contributor/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (subRes?.ref_code) {
        try { localStorage.setItem("in21_my_ref", subRes.ref_code); } catch {}
        setMyRefCode(subRes.ref_code);
      }
      toast.success("धन्यवाद! आपकी रिपोर्ट संपादक के पास पहुँच गई है — मंज़ूरी मिलते ही यहाँ प्रकाशित होगी।");
      setForm({ name: "", phone: "", city: "", text: "" });
      setPhoto(null); setPreview("");
      if (fileRef.current) fileRef.current.value = "";
      load(activeCity);
      setShowInvite(true);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      <PublicHeader />
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#7C2D12] via-[#B45309] to-[#EA580C] text-white" data-testid="contrib-hero">
        <div className="max-w-6xl mx-auto px-4 py-14 flex flex-col md:flex-row items-start gap-8">
          <div className="flex-1">
            <div className="uppercase tracking-[0.28em] text-[10px] text-amber-100 font-black mb-2 flex items-center gap-2">
              <Heart className="w-3.5 h-3.5"/> Reader Contributor Wall
            </div>
            <h1 className="font-devnagri-heading text-4xl md:text-5xl font-black leading-tight max-w-2xl">
              अब हर नागरिक है <span className="text-amber-200">पत्रकार</span>
            </h1>
            <p className="text-amber-50/95 mt-4 max-w-xl leading-relaxed">
              अपने गाँव, शहर या मोहल्ले की ख़बर, फ़ोटो या वीडियो हमें भेजिए। संपादक स्वयं समीक्षा करेंगे और चयनित रिपोर्ट INDIAN-NEWS-21 पर प्रकाशित होगी — आपके नाम के साथ।
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-xs">
              <Stat label="कुल प्रकाशित" value={wall.length}/>
              <Stat label="चयन आधार" value="संपादकीय"/>
              <Stat label="प्रकाशन" value="24 घंटे में"/>
            </div>
          </div>
          <div className="hidden md:block shrink-0 relative">
            <div className="absolute -inset-1 bg-amber-300/60 rounded-full blur-md"></div>
            <img src="/jakir-momin-boss.png" alt="Chief Editor" className="relative w-28 h-28 rounded-full object-cover border-4 border-amber-200 shadow-2xl"/>
            <div className="text-center mt-2 text-[10px] uppercase tracking-widest text-amber-100 font-bold">Chief Editor Reviews</div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 pt-8">
        <FeaturedWeekCard />
      </div>

      <main className="max-w-6xl mx-auto px-4 pb-12 grid grid-cols-1 lg:grid-cols-5 gap-10" data-testid="contrib-page">
        {/* Form */}
        <form ref={formRef} onSubmit={submit} className="lg:col-span-2 bg-white rounded-xl border-2 border-amber-200 p-6 shadow-sm h-fit sticky top-24 space-y-4" data-testid="contrib-form">
          <h2 className="font-devnagri-heading text-xl font-black text-[#0F172A] flex items-center gap-2">
            <Send className="w-5 h-5 text-[#EA580C]"/> अपनी रिपोर्ट भेजें
          </h2>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-admin text-slate-500 font-bold mb-1 flex items-center gap-1"><User className="w-3 h-3"/>आपका नाम *</label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="पूरा नाम" required maxLength={80} data-testid="contrib-name"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-widest font-admin text-slate-500 font-bold mb-1 flex items-center gap-1"><Phone className="w-3 h-3"/>फ़ोन</label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="वैकल्पिक" data-testid="contrib-phone"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-admin text-slate-500 font-bold mb-1 flex items-center gap-1"><MapPin className="w-3 h-3"/>शहर/गाँव</label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="वैकल्पिक" data-testid="contrib-city"/>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-admin text-slate-500 font-bold mb-1 block">विवरण / रिपोर्ट *</label>
            <Textarea rows={5} value={form.text} onChange={(e) => set("text", e.target.value)} placeholder="कहानी विस्तार से लिखें…" required maxLength={1500} data-testid="contrib-text"/>
            <div className="text-[10px] text-slate-400 mt-1 text-right">{form.text.length}/1500</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-admin text-slate-500 font-bold mb-1 block">फ़ोटो (वैकल्पिक)</label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickPhoto(e.target.files?.[0])} data-testid="contrib-file"/>
            <div onClick={() => fileRef.current?.click()} className="cursor-pointer rounded-md border-2 border-dashed border-slate-300 hover:border-[#EA580C] p-3 flex items-center gap-3 bg-slate-50">
              {preview ? (
                <img src={preview} alt="" className="w-16 h-16 rounded-md object-cover"/>
              ) : (
                <div className="w-16 h-16 rounded-md bg-white border border-slate-200 flex items-center justify-center text-slate-400">
                  <ImageIcon className="w-6 h-6"/>
                </div>
              )}
              <div className="text-xs text-slate-500 flex-1">
                <div className="font-bold text-slate-700 flex items-center gap-1"><Upload className="w-3 h-3"/>{photo ? photo.name : "फ़ोटो अपलोड करें"}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">JPG/PNG · अधिकतम 6MB</div>
              </div>
            </div>
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-[#EA580C] to-[#DC2626] hover:from-[#DC2626] hover:to-[#EA580C] text-white font-bold" data-testid="contrib-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Send className="w-4 h-4 mr-2"/>}
            रिपोर्ट भेजें
          </Button>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            नोट: सभी रिपोर्ट संपादकीय समीक्षा के बाद ही प्रकाशित होती हैं। भड़काऊ, अश्लील, अफ़वाह या धार्मिक विवाद फैलाने वाली सामग्री अस्वीकृत कर दी जाएगी।
          </p>
        </form>

        {/* Wall */}
        <section className="lg:col-span-3">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-devnagri-heading text-2xl font-black text-[#0F172A]">प्रकाशित योगदान</h2>
            <div className="h-px bg-slate-200 flex-1"></div>
            <span className="text-xs text-slate-500 font-bold">{wall.length}</span>
          </div>

          {/* City filter chips */}
          {cities.length > 0 && (
            <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="city-chips">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-1">मेरे शहर की खबर:</span>
              <button
                onClick={() => pickCity("")}
                className={`px-3 py-1 rounded-full text-xs font-bold transition ${
                  !activeCity ? "bg-[#0F172A] text-white shadow" : "bg-white border border-slate-200 text-slate-600 hover:border-[#EA580C] hover:text-[#EA580C]"
                }`}
                data-testid="city-chip-all">
                सभी
              </button>
              {cities.map((c) => (
                <button
                  key={c.city}
                  onClick={() => pickCity(c.city)}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition inline-flex items-center gap-1.5 ${
                    activeCity.toLowerCase() === c.city.toLowerCase()
                      ? "bg-[#EA580C] text-white shadow"
                      : "bg-white border border-slate-200 text-slate-600 hover:border-[#EA580C] hover:text-[#EA580C]"
                  }`}
                  data-testid={`city-chip-${c.city}`}>
                  📍 {c.city}
                  <span className={`text-[9px] rounded-full px-1.5 py-0.5 ${activeCity.toLowerCase() === c.city.toLowerCase() ? "bg-white/25" : "bg-slate-100"}`}>{c.count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Active city weather + AQI + quick submit */}
          <CityWeatherCard city={activeCity} onQuickSubmit={quickSubmit} />

          {/* Top Contributors + गौरव पाठक Leaderboard */}
          <GauravLeaderboard />

          {loading ? (
            <div className="text-slate-500 py-16 text-center">लोड हो रहा है…</div>
          ) : wall.length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-slate-300 rounded-lg">
              <Heart className="w-14 h-14 text-slate-300 mx-auto mb-3"/>
              <div className="font-bold text-slate-700">
                {activeCity ? `"${activeCity}" से कोई योगदान नहीं मिला` : "अभी तक कोई योगदान प्रकाशित नहीं हुआ"}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {activeCity ? "किसी और शहर की खबर चुनें या पहले योगदानकर्ता बनें!" : "पहला योगदान आप ही भेजिए!"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="contrib-wall">
              {wall.map((c) => (
                <article key={c.id} className={`bg-white rounded-lg border overflow-hidden hover:shadow-md transition ${c.editor_pick ? "border-amber-400 shadow-amber-100 shadow-md ring-2 ring-amber-200" : "border-slate-200"}`} data-testid={`contrib-item-${c.id}`}>
                  {c.editor_pick && (
                    <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 text-white text-[10px] font-black uppercase tracking-[0.22em] px-3 py-1.5 flex items-center gap-2">
                      <img src="/jakir-momin-boss.png" alt="" className="w-5 h-5 rounded-full object-cover border border-white/40"/>
                      <span>🌟 प्रधान संपादक की सिफ़ारिश</span>
                    </div>
                  )}
                  {c.photo_url && (
                    <div className="aspect-video bg-slate-100">
                      <img src={c.photo_url.startsWith("http") ? c.photo_url : `${API.replace(/\/api$/, "")}${c.photo_url}`} alt="" className="w-full h-full object-cover"/>
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-black text-xs">
                        {c.name?.[0] || "?"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-sm text-[#0F172A] truncate">{c.name}</div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {c.city ? `📍 ${c.city}` : ""}
                          {c.city && c.approved_at ? " · " : ""}
                          {c.approved_at ? new Date(c.approved_at).toLocaleDateString("hi-IN", { day: "numeric", month: "short" }) : ""}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed line-clamp-6 whitespace-pre-wrap">{c.text}</p>

                    {c.editor_reply && (
                      <div className="mt-3 relative bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`editor-reply-${c.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <img src="/jakir-momin-boss.png" alt="JAKIR MOMIN" className="w-6 h-6 rounded-full object-cover border-2 border-amber-300"/>
                          <div className="text-[9px] uppercase tracking-widest text-amber-800 font-black">प्रधान संपादक JAKIR MOMIN का जवाब</div>
                        </div>
                        <p className="text-[13px] text-slate-800 italic leading-relaxed pl-1">"{c.editor_reply}"</p>
                      </div>
                    )}

                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => clap(c.id)}
                        disabled={clapped.has(c.id)}
                        className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 border transition ${
                          clapped.has(c.id)
                            ? "bg-amber-100 border-amber-300 text-amber-800 cursor-default"
                            : "bg-white border-slate-200 text-slate-700 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 active:scale-95"
                        }`}
                        title={clapped.has(c.id) ? "ताली दी गई" : "ताली बजाइये"}
                        data-testid={`clap-${c.id}`}>
                        <Hand className={`w-3.5 h-3.5 ${clapped.has(c.id) ? "fill-amber-500 text-amber-600" : ""}`}/>
                        <span>{(clapCounts[c.id] ?? c.claps) || 0}</span>
                      </button>
                      {c.city && (
                        <button
                          onClick={() => pickCity(c.city)}
                          className="text-[10px] uppercase tracking-widest font-bold text-[#EA580C] hover:underline"
                          data-testid={`item-city-${c.city}`}>
                          📍 {c.city} की और खबरें →
                        </button>
                      )}
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(`📢 INDIAN-NEWS-21 · जनता की आवाज़\n\n"${c.text.slice(0, 240)}${c.text.length > 240 ? "…" : ""}"\n\n— ${c.name}${c.city ? `, ${c.city}` : ""}\n\nऐसी और खबरें पढ़ें: ${typeof window !== "undefined" ? window.location.origin : ""}/contribute`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-full px-2.5 py-1 transition"
                        title="WhatsApp पर शेयर करें"
                        data-testid={`share-wa-${c.id}`}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-600"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.76.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22c5.46 0 9.91-4.45 9.91-9.93S17.5 2 12.04 2zm5.72 14.16c-.24.67-1.4 1.28-1.94 1.32-.52.04-1.02.22-3.44-.75-2.87-1.14-4.75-4.04-4.9-4.24-.15-.2-1.18-1.58-1.18-3.02s.76-2.14 1.03-2.44c.27-.3.6-.37.8-.37h.57c.18 0 .43-.07.68.52.24.6.83 2.06.9 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.32.38-.45.51-.15.15-.3.32-.13.62.17.3.75 1.24 1.62 2 1.12 1 2.07 1.31 2.37 1.46.3.15.47.13.65-.07.17-.2.75-.87.95-1.17.2-.3.4-.25.67-.15.27.1 1.72.81 2.02.96.3.15.5.22.57.35.07.13.07.72-.17 1.4z"/></svg>
                        शेयर
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
      <PublicFooter />

      {/* Referral Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setShowInvite(false)} data-testid="invite-modal">
          <div className="relative bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowInvite(false)} className="absolute top-3 right-3 p-2 rounded-full hover:bg-slate-100 z-10" data-testid="invite-close">
              <X className="w-5 h-5 text-slate-500"/>
            </button>
            <div className="bg-gradient-to-br from-[#EA580C] via-[#DC2626] to-[#B45309] text-white p-6 text-center">
              <div className="mx-auto mb-3 w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <Check className="w-8 h-8 text-white"/>
              </div>
              <div className="uppercase tracking-[0.28em] text-[10px] font-black text-amber-100 mb-2">रिपोर्ट प्राप्त!</div>
              <h3 className="font-devnagri-heading text-2xl font-black leading-tight">धन्यवाद, आपकी आवाज़ पहुँची</h3>
              <p className="text-amber-50 text-sm mt-2 leading-relaxed">
                संपादक 24 घंटे में समीक्षा करेंगे — मंज़ूरी मिलते ही आपका नाम पोर्टल पर छपेगा
              </p>
            </div>
            <div className="p-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="font-devnagri-heading font-black text-[#0F172A] text-base leading-tight mb-1">
                  🎁 अपने 3 दोस्तों को भी बुलाइए
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  जितने ज़्यादा लोग योगदान देंगे, उतनी जल्दी सम्पादक आपकी रिपोर्ट देखेंगे। WhatsApp पर एक क्लिक में शेयर कीजिये —
                </p>
              </div>
              {(() => {
                const base = typeof window !== "undefined" ? `${window.location.origin}/contribute` : "";
                const shareUrl = myRefCode ? `${base}?ref=${myRefCode}` : base;
                const inviteText = `🎙️ INDIAN-NEWS-21 · भारत की आवाज़\n\nमैंने अभी अपने शहर की एक ख़बर भेजी — अब आप भी अपने मोहल्ले, गाँव या शहर की खबर, फ़ोटो या रिपोर्ट भेजिए। प्रधान संपादक JAKIR MOMIN सर ख़ुद समीक्षा करेंगे और आपके नाम से प्रकाशित करेंगे।\n\n👉 अभी योगदान भेजें: ${shareUrl}`;
                const waHref = `https://wa.me/?text=${encodeURIComponent(inviteText)}`;
                const copy = async () => {
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    setCopied(true);
                    toast.success("लिंक कॉपी हो गया");
                    setTimeout(() => setCopied(false), 2000);
                  } catch { toast.error("कॉपी नहीं हो सका"); }
                };
                return (
                  <div className="space-y-2">
                    <a href={waHref} target="_blank" rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold text-sm px-4 py-3 rounded-md shadow transition"
                      data-testid="invite-wa"
                      onClick={() => setTimeout(() => setShowInvite(false), 500)}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.76.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22c5.46 0 9.91-4.45 9.91-9.93S17.5 2 12.04 2zm5.72 14.16c-.24.67-1.4 1.28-1.94 1.32-.52.04-1.02.22-3.44-.75-2.87-1.14-4.75-4.04-4.9-4.24-.15-.2-1.18-1.58-1.18-3.02s.76-2.14 1.03-2.44c.27-.3.6-.37.8-.37h.57c.18 0 .43-.07.68.52.24.6.83 2.06.9 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.32.38-.45.51-.15.15-.3.32-.13.62.17.3.75 1.24 1.62 2 1.12 1 2.07 1.31 2.37 1.46.3.15.47.13.65-.07.17-.2.75-.87.95-1.17.2-.3.4-.25.67-.15.27.1 1.72.81 2.02.96.3.15.5.22.57.35.07.13.07.72-.17 1.4z"/></svg>
                      WhatsApp पर 3 दोस्तों को भेजें
                    </a>
                    <button onClick={copy} className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-200 hover:border-[#EA580C] hover:text-[#EA580C] text-slate-700 font-bold text-sm px-4 py-3 rounded-md transition" data-testid="invite-copy">
                      {copied ? <Check className="w-4 h-4 text-emerald-600"/> : <Copy className="w-4 h-4"/>}
                      {copied ? "कॉपी हो गया!" : "लिंक कॉपी करें"}
                    </button>
                    <div className="text-[10px] text-center text-slate-500 pt-2 border-t border-slate-100 mt-3">
                      💡 आपके 3 friend जुड़े तो <b className="text-[#EA580C]">"गौरव पाठक"</b> बैज मिलेगा
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white/15 backdrop-blur rounded-md px-3 py-1.5 border border-white/20">
      <div className="text-[9px] uppercase tracking-widest text-amber-100/90 font-bold">{label}</div>
      <div className="text-base font-black text-white">{value}</div>
    </div>
  );
}
